/* ========================================================================
   © 2026 FixMySiteKit Framework. All rights reserved.
   Directory Branch — /solutions/:niche/:location
   Final SEO branch page. Injects LocalBusiness JSON-LD schema.
   ======================================================================== */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
    AlertCircle,
    ArrowRight,
    CheckCircle2,
    Loader2,
    MapPin,
    Phone,
    Radar,
    Send,
    ShieldCheck,
    Timer,
    Users,
    Zap,
} from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import DirectoryLoading from "@/components/DirectoryLoading";
import useSeoHead from "@/lib/useSeoHead";
import { API_BASE } from "@/lib/api";

const CANONICAL_HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Deterministic per-branch neighborhood picker. Uses a simple string hash so
 * the same branch always renders the same 5 selections — critical for stable
 * SEO snapshots and to avoid hydration mismatches.
 */
function pickRandomNeighborhoods(list, count) {
    if (!Array.isArray(list) || list.length === 0) return [];
    if (list.length <= count) return list;
    const seed = list.join("|");
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
        h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    }
    const picked = [];
    const used = new Set();
    let cursor = h;
    while (picked.length < count && used.size < list.length) {
        cursor = (cursor * 1103515245 + 12345) >>> 0;
        const idx = cursor % list.length;
        if (!used.has(idx)) {
            used.add(idx);
            picked.push(list[idx]);
        }
    }
    return picked;
}

export default function DirectoryBranch() {
    const { niche, location } = useParams();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, branch: null, aiProse: null });
    const [form, setForm] = useState({ name: "", phone: "", notes: "", websiteUrlCheck: "" });
    const [submit, setSubmit] = useState({ status: "idle", message: "" });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/directory/branch/${niche}/${location}`,
                    { credentials: "omit" }
                );
                if (res.status === 404) {
                    if (!cancelled) navigate("/404-directory", { replace: true });
                    return;
                }
                const data = await res.json();
                if (!cancelled) setState({
                    loading: false,
                    branch: data.branch,
                    aiProse: data.aiProse || null,
                });
            } catch (_) {
                if (!cancelled) setState({ loading: false, branch: null, aiProse: null });
            }
        })();
        return () => { cancelled = true; };
    }, [niche, location, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submit.status === "submitting") return;
        setSubmit({ status: "submitting", message: "" });
        try {
            const res = await fetch(`${API_BASE}/directory/lead`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "omit",
                body: JSON.stringify({
                    nicheSlug: niche,
                    locationSlug: location,
                    name: form.name,
                    phone: form.phone,
                    notes: form.notes,
                    websiteUrlCheck: form.websiteUrlCheck || "",
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.status === 429) {
                setSubmit({ status: "error", message: "Too many requests from this network. Please try again in a few minutes." });
                return;
            }
            if (!res.ok) {
                setSubmit({ status: "error", message: data.detail || "Something went wrong. Please try again." });
                return;
            }
            setSubmit({
                status: "success",
                message: data.message || "Request logged.",
            });
            setForm({ name: "", phone: "", notes: "" });
        } catch (_) {
            setSubmit({ status: "error", message: "Network error. Please try again." });
        }
    };

    const b = state.branch;
    const canonical = `${CANONICAL_HOST}/solutions/${niche}/${location}`;

    useSeoHead({
        title: b
            ? `${b.nicheLabel} in ${b.locationLabel} — ${b.responseTimeSlaMinutes}-Min Dispatch | FixMySiteKit`
            : "Verified Local Branch | FixMySiteKit",
        description: b
            ? `Verified ${b.nicheLabel.toLowerCase()} branch serving ${b.metroCity}, ${b.metroState} and surrounding areas. ${b.operatorCount} operators on rotation. Response inside ${b.responseTimeSlaMinutes} minutes. Web-form to phone call in under 30 seconds.`
            : "Verified local branch.",
        canonical,
        keywords: b
            ? [
                  `${b.nicheLabel} ${b.metroCity}`,
                  `${b.metroCity} ${b.nicheShortLabel}`,
                  ...(b.nicheKeywords || []).map((k) => `${k} ${b.metroCity}`),
              ].join(", ")
            : "",
        jsonLd: b
            ? [
                  {
                      "@context": "https://schema.org",
                      "@type": "LocalBusiness",
                      name: `${b.nicheLabel} — ${b.locationLabel} Branch`,
                      description: `Verified FixMySiteKit ${b.nicheLabel.toLowerCase()} dispatch covering ${b.metroCity} and surrounding neighborhoods.`,
                      url: canonical,
                      areaServed: {
                          "@type": "City",
                          name: `${b.metroCity}, ${b.metroState}`,
                      },
                      address: {
                          "@type": "PostalAddress",
                          addressLocality: b.metroCity,
                          addressRegion: b.metroState,
                          addressCountry: "US",
                      },
                      priceRange: b.avgTicket,
                      openingHours: "Mo-Su 00:00-23:59",
                      makesOffer: {
                          "@type": "Offer",
                          itemOffered: { "@type": "Service", name: b.nicheLabel },
                      },
                  },
                  {
                      "@context": "https://schema.org",
                      "@type": "FAQPage",
                      mainEntity: (b.faqs || []).map((f) => ({
                          "@type": "Question",
                          name: f.q,
                          acceptedAnswer: { "@type": "Answer", text: f.a },
                      })),
                  },
                  {
                      "@context": "https://schema.org",
                      "@type": "BreadcrumbList",
                      itemListElement: [
                          {
                              "@type": "ListItem",
                              position: 1,
                              name: "Directory",
                              item: `${CANONICAL_HOST}/solutions`,
                          },
                          {
                              "@type": "ListItem",
                              position: 2,
                              name: b.nicheLabel,
                              item: `${CANONICAL_HOST}/solutions/${niche}`,
                          },
                          {
                              "@type": "ListItem",
                              position: 3,
                              name: b.locationLabel,
                              item: canonical,
                          },
                      ],
                  },
              ]
            : null,
    });

    if (state.loading) return <DirectoryLoading label="Loading branch…" />;
    if (!b) {
        return (
            <div className="mx-auto max-w-2xl px-6 py-24 text-center">
                <h1 className="font-display text-2xl font-bold">Branch not found</h1>
                <Link to="/solutions" className="mt-4 inline-block text-primary underline">
                    Back to directory
                </Link>
            </div>
        );
    }

    return (
        <div
            data-testid="directory-branch"
            className="min-h-screen bg-background text-foreground"
        >
            <header
                className="border-b border-border bg-background/85 backdrop-blur"
                role="banner"
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
                    <Link to="/" className="inline-flex items-center gap-2" aria-label="FixMySiteKit home">
                        <span className="grid h-8 w-8 place-items-center bg-primary text-primary-foreground">
                            <Zap className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span className="font-display text-lg font-bold tracking-tight">FixMySiteKit</span>
                    </Link>
                    <Link
                        to={`/solutions/${niche}`}
                        data-testid="branch-back-link"
                        className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground"
                    >
                        ← {b.nicheLabel}
                    </Link>
                </div>
            </header>

            <main role="main">
                <section aria-labelledby="branch-heading" className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-16 md:px-10 md:py-20">
                        <nav aria-label="Breadcrumb" className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
                            <Link to="/solutions" className="hover:text-foreground">
                                Directory
                            </Link>{" "}
                            /{" "}
                            <Link to={`/solutions/${niche}`} className="hover:text-foreground">
                                {b.nicheLabel}
                            </Link>{" "}
                            / <span className="text-primary">{b.locationLabel}</span>
                        </nav>
                        <h1
                            id="branch-heading"
                            data-testid="directory-branch-heading"
                            className="mt-3 max-w-4xl font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
                        >
                            {b.nicheLabel} in{" "}
                            <span className="text-primary">{b.locationLabel}</span>
                        </h1>
                        <p
                            data-testid="branch-lede"
                            className="mt-6 max-w-3xl text-base text-muted-foreground md:text-lg"
                        >
                            Verified {b.nicheShortLabel.toLowerCase()} on rotation for {b.metroCity} and surrounding
                            metros. When a web form fires, the FixMySiteKit relay places an
                            outbound call to the on-duty operator in under 30 seconds — average
                            hand-off inside <span className="text-foreground">{b.responseTimeSlaMinutes} minutes</span>.
                        </p>

                        <dl className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <div className="border border-border bg-card p-5">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                    <Timer className="mr-1.5 inline h-3 w-3" aria-hidden="true" />
                                    Response SLA
                                </dt>
                                <dd className="mt-2 font-display text-2xl font-bold">
                                    {b.responseTimeSlaMinutes} min
                                </dd>
                            </div>
                            <div className="border border-border bg-card p-5">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                    <MapPin className="mr-1.5 inline h-3 w-3" aria-hidden="true" />
                                    Coverage radius
                                </dt>
                                <dd className="mt-2 font-display text-2xl font-bold">
                                    {b.serviceRadius}
                                </dd>
                            </div>
                            <div className="border border-border bg-card p-5">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                    <ShieldCheck className="mr-1.5 inline h-3 w-3" aria-hidden="true" />
                                    Operators on rotation
                                </dt>
                                <dd className="mt-2 font-display text-2xl font-bold">
                                    {b.operatorCount}
                                </dd>
                            </div>
                            <div className="border border-border bg-card p-5">
                                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                    <Phone className="mr-1.5 inline h-3 w-3" aria-hidden="true" />
                                    Avg ticket
                                </dt>
                                <dd className="mt-2 font-display text-2xl font-bold">
                                    {b.avgTicket}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </section>

                {/* AI-generated local SEO prose — one unique paragraph per
                    niche × metro combo, cached in the seo_prose collection.
                    Renders when populated; otherwise the page falls through
                    to the existing hash-picked snippets so nothing regresses. */}
                {state.aiProse && (
                    <section
                        aria-labelledby="local-prose-heading"
                        className="border-b border-border"
                        data-testid="branch-ai-prose-section"
                    >
                        <div className="mx-auto max-w-4xl px-6 py-14 md:px-10">
                            <h2
                                id="local-prose-heading"
                                className="font-display text-xl font-bold tracking-tight md:text-2xl text-foreground/90"
                            >
                                On the ground in {b.metroCity}
                            </h2>
                            <p
                                data-testid="branch-ai-prose"
                                className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed"
                            >
                                {state.aiProse}
                            </p>
                        </div>
                    </section>
                )}

                <section aria-labelledby="hoods-heading" className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <h2
                            id="hoods-heading"
                            className="font-display text-2xl font-bold tracking-tight md:text-3xl"
                        >
                            Neighborhoods this branch covers
                        </h2>
                        <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
                            The {b.metroCity} {b.nicheShortLabel.toLowerCase()} rotation dispatches inside
                            a {b.serviceRadius} radius, including:
                        </p>
                        {(b.neighborhoods || []).length >= 5 && (
                            <p
                                className="mt-3 max-w-3xl text-sm text-muted-foreground"
                                data-testid="branch-serving-copy"
                            >
                                Serving nearby homeowners in{" "}
                                <span className="text-foreground font-semibold">
                                    {pickRandomNeighborhoods(b.neighborhoods, 5).join(", ")}
                                </span>
                                , and surrounding {b.metroCity} communities.
                            </p>
                        )}
                        <ul
                            className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
                            data-testid="branch-neighborhoods"
                        >
                            {(b.neighborhoods || []).map((hood) => (
                                <li
                                    key={hood}
                                    className="border border-border bg-card px-4 py-3 font-mono text-sm uppercase tracking-[0.15em] text-foreground"
                                >
                                    {hood}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* Why local matters + radius explainer */}
                <section
                    aria-labelledby="why-local-heading"
                    className="border-b border-border"
                >
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <h2
                            id="why-local-heading"
                            className="font-display text-2xl font-bold tracking-tight md:text-3xl"
                        >
                            Why local dispatch wins in {b.metroCity}
                        </h2>
                        <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="branch-why-local">
                            <article className="border border-border bg-card p-6">
                                <div className="inline-flex h-10 w-10 items-center justify-center bg-primary/10 text-primary">
                                    <Users className="h-5 w-5" aria-hidden="true" />
                                </div>
                                <h3 className="mt-4 font-display text-lg font-bold">
                                    Your call rings one phone — never auctioned
                                </h3>
                                <p className="mt-3 text-sm text-muted-foreground">
                                    National lead-gen marketplaces resell the same {b.nicheShortLabel.toLowerCase()} request
                                    to 4–8 competing companies. The FixMySiteKit relay rings only the
                                    verified {b.metroCity} operator on rotation, and stops the loop the
                                    second the call connects. Zero shared leads, zero per-lead fees.
                                </p>
                            </article>
                            <article className="border border-border bg-card p-6">
                                <div className="inline-flex h-10 w-10 items-center justify-center bg-primary/10 text-primary">
                                    <ShieldCheck className="h-5 w-5" aria-hidden="true" />
                                </div>
                                <h3 className="mt-4 font-display text-lg font-bold">
                                    Local codes, local licenses, local accountability
                                </h3>
                                <p className="mt-3 text-sm text-muted-foreground">
                                    Every {b.metroCity} {b.nicheShortLabel.toLowerCase()} in the rotation is
                                    licensed for {b.metroState} jurisdictions, insured for the {b.serviceRadius}{" "}
                                    dispatch zone, and reachable at a real business address — not a
                                    call-center IVR forwarding to whoever bid highest that hour.
                                </p>
                            </article>
                        </div>

                        <div className="mt-10 border border-border bg-card p-6 md:p-8" data-testid="branch-radius-explainer">
                            <div className="flex items-start gap-4">
                                <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center bg-primary/10 text-primary">
                                    <Radar className="h-5 w-5" aria-hidden="true" />
                                </div>
                                <div>
                                    <h3 className="font-display text-lg font-bold">
                                        How the {b.serviceRadius} service radius works
                                    </h3>
                                    <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
                                        The {b.metroCity} branch operates a {b.serviceRadius} dispatch ring
                                        with {b.operatorCount} operators on staggered rotation. When your
                                        request hits the relay, the router pings the nearest available
                                        operator inside that ring — typical hand-off completes in{" "}
                                        <span className="text-foreground">{b.responseTimeSlaMinutes} minutes</span>,
                                        with a hard ceiling of 30 seconds between form-submit and phone-ring.
                                    </p>
                                    <p className="mt-3 max-w-3xl text-sm text-muted-foreground">
                                        Outside the {b.serviceRadius} ring, requests roll to the nearest
                                        adjacent branch automatically. No orphaned leads, no dead-air
                                        voicemail routing.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Inline lead-capture form */}
                <section
                    id="request-form"
                    aria-labelledby="lead-form-heading"
                    className="border-b border-border"
                >
                    <div className="mx-auto grid max-w-7xl gap-10 px-6 py-14 md:grid-cols-5 md:px-10">
                        <div className="md:col-span-2">
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                                Request Instant Connect
                            </p>
                            <h2
                                id="lead-form-heading"
                                className="mt-3 font-display text-2xl font-bold tracking-tight md:text-3xl"
                            >
                                Get a {b.metroCity} {b.nicheShortLabel.toLowerCase().replace(/s$/, "")} callback.
                            </h2>
                            <p className="mt-4 text-sm text-muted-foreground">
                                Drop your number and a one-line description. The {b.metroCity} branch
                                dispatcher reviews inbound requests directly — no automated calling,
                                no bot outreach, no upsells.
                            </p>
                            <ul className="mt-6 space-y-2 text-xs text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                                    Request goes to a human dispatcher
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                                    No shared / resold leads
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
                                    Rate-limited & spam-protected
                                </li>
                            </ul>
                        </div>

                        <form
                            onSubmit={handleSubmit}
                            className="md:col-span-3"
                            data-testid="branch-lead-form"
                            aria-label={`Request a ${b.nicheLabel} callback in ${b.locationLabel}`}
                        >
                            <div className="border border-border bg-card p-6 md:p-8">
                                {/* Honeypot — hidden input, invisible to real users. */}
                                <input
                                    type="text"
                                    name="website_url_check"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                    value={form.websiteUrlCheck}
                                    onChange={(e) => setForm({ ...form, websiteUrlCheck: e.target.value })}
                                    style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0, pointerEvents: "none" }}
                                    data-testid="branch-lead-honeypot"
                                />
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="sm:col-span-1">
                                        <label
                                            htmlFor="lead-name"
                                            className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground"
                                        >
                                            Full name
                                        </label>
                                        <input
                                            id="lead-name"
                                            data-testid="lead-name-input"
                                            type="text"
                                            required
                                            minLength={2}
                                            maxLength={120}
                                            autoComplete="name"
                                            value={form.name}
                                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                                            className="mt-2 block w-full border border-border bg-background px-3 py-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
                                            placeholder="Jane Doe"
                                        />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label
                                            htmlFor="lead-phone"
                                            className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground"
                                        >
                                            Phone number
                                        </label>
                                        <input
                                            id="lead-phone"
                                            data-testid="lead-phone-input"
                                            type="tel"
                                            required
                                            minLength={6}
                                            maxLength={24}
                                            autoComplete="tel"
                                            value={form.phone}
                                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                            className="mt-2 block w-full border border-border bg-background px-3 py-3 font-mono text-sm text-foreground outline-none transition-colors focus:border-primary"
                                            placeholder="(555) 019-2834"
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label
                                            htmlFor="lead-notes"
                                            className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground"
                                        >
                                            What’s going on? (optional)
                                        </label>
                                        <textarea
                                            id="lead-notes"
                                            data-testid="lead-notes-input"
                                            rows={3}
                                            maxLength={1200}
                                            value={form.notes}
                                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                            className="mt-2 block w-full resize-none border border-border bg-background px-3 py-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
                                            placeholder={`e.g. ${b.nicheProblem?.split(",")[0] || "brief description"}`}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    data-testid="lead-submit-button"
                                    disabled={submit.status === "submitting" || submit.status === "success"}
                                    className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-primary px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                                >
                                    {submit.status === "submitting" ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            Sending…
                                        </>
                                    ) : submit.status === "success" ? (
                                        <>
                                            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                                            Request received
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4" aria-hidden="true" />
                                            Request {b.metroCity} callback
                                        </>
                                    )}
                                </button>

                                <div
                                    aria-live="polite"
                                    className="mt-4 min-h-[1.25rem]"
                                    data-testid="lead-form-feedback"
                                >
                                    {submit.status === "success" && (
                                        <p className="flex items-start gap-2 text-xs text-primary">
                                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                            {submit.message}
                                        </p>
                                    )}
                                    {submit.status === "error" && (
                                        <p className="flex items-start gap-2 text-xs text-destructive">
                                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                                            {submit.message}
                                        </p>
                                    )}
                                </div>

                                <p className="mt-4 text-[10px] text-muted-foreground">
                                    By submitting, you agree that FixMySiteKit may log your request for
                                    dispatcher review. We never resell leads.
                                </p>
                            </div>
                        </form>
                    </div>
                </section>

                <section aria-labelledby="faq-heading" className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <h2
                            id="faq-heading"
                            className="font-display text-2xl font-bold tracking-tight md:text-3xl"
                        >
                            Frequently asked
                        </h2>
                        <div className="mt-8 grid gap-6 md:grid-cols-2" data-testid="branch-faqs">
                            {(b.faqs || []).map((f, idx) => (
                                <article key={idx} className="border border-border bg-card p-6">
                                    <h3 className="font-display text-base font-bold">{f.q}</h3>
                                    <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="border-b border-border">
                    <div className="mx-auto max-w-7xl px-6 py-14 md:px-10">
                        <div className="border border-border bg-card p-8 md:p-10">
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
                                Own {b.metroCity}’s speed-to-lead
                            </p>
                            <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight md:text-3xl">
                                Route your {b.metroCity} {b.nicheLabel.toLowerCase()} leads through the same relay.
                            </h2>
                            <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                                $149 one-time setup · $79 / month · Refund guaranteed if the relay
                                never places a call.
                            </p>
                            <Link
                                to="/"
                                data-testid="branch-cta-funnel"
                                className="mt-6 inline-flex items-center gap-2 bg-primary px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-primary-foreground transition-transform hover:-translate-y-0.5"
                            >
                                Activate my branch
                                <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <SiteFooter />
        </div>
    );
}
